import { ingredientMoves, type IngredientWorld } from './ingredients'
import { soldFromShelf } from './service'
import type { Product } from './mock-data'

/**
 * 182 (1.59) — how a till's copy of the shelf follows a sale or a return.
 *
 * Since a drink takes its cup on the SERVER, the cart no longer says what left
 * the shelf: the reply's lines do (the drink's own and the ₾0 ones it took).
 * The store moves its copy by those lines, never by its own copy of the recipe,
 * which another device may have changed (Codex's design review #6). These are
 * the two decisions that move it, kept here so they are tested without a store.
 */

/** What was on screen when a request left (the createExchange guard). */
export type ShelfStamp = {
  gen: number
  orgId: string | undefined
  branchId: string | null
  applied: number
}

/**
 * What the reply may do to the cached shelf:
 * - another shop, session or branch took the screen → nothing (it is not that shelf);
 * - a replay (moved with the first press) or a live read applied meanwhile (it may
 *   already hold this sale) → read the shelf again, never move it twice;
 * - otherwise → move it by the reply's lines.
 */
export function replyShelfPlan(before: ShelfStamp, now: ShelfStamp, replayed: boolean): 'delta' | 'refresh' | 'none' {
  if (before.gen !== now.gen || before.orgId !== now.orgId || before.branchId !== now.branchId) return 'none'
  if (replayed || before.applied !== now.applied) return 'refresh'
  return 'delta'
}

/**
 * The shelf after a sale queued offline: the cart's lines and what its recipes
 * take. Only an ESTIMATE — the server writes the true lines when the queue
 * drains, and the next online boot reads the shelf again.
 */
export function offlineShelf(
  world: IngredientWorld,
  lines: { product_id?: string | null; quantity: number }[],
): Product[] {
  return soldFromShelf(world.products, [...lines, ...ingredientMoves(world, lines)], false)
}
